import React from 'react';
import { useTaxForm, T } from '../TaxFormContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus } from 'lucide-react';
import { useLiveTaxEngine } from '../useLiveTaxEngine';

const toArray = (val: any) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
};

export const Step5AssetsLiabilities: React.FC = () => {
  const { formData, updateFormData, lang } = useTaxForm();
  
  // Call the hook just to keep computed fields syncing if they modify things here
  useLiveTaxEngine();

  const liabilitiesArray = toArray(formData.liabilities_details);

  const arrayHandlers = {
      addLiability: () => updateFormData({ liabilities_details: [...liabilitiesArray, {id: Date.now()+Math.random(), type: 'Bank Loan', description: '', amount: 0}] }),
      removeLiability: (id: any) => updateFormData({ liabilities_details: liabilitiesArray.filter((l:any) => l.id !== id) }),
      updateLiability: (id: any, field: string, value: any) => updateFormData({ liabilities_details: liabilitiesArray.map((l:any) => l.id === id ? { ...l, [field]: value } : l) }),
  };

  return (
    <div className="space-y-6 animate-fade-in relative z-10 w-full max-w-full">
      <div className="bg-rose-50/50 backdrop-blur-sm border border-rose-100/60 p-6 md:p-8 rounded-[2rem] shadow-sm space-y-6">
        <p className="text-rose-700/80 font-medium leading-relaxed">
            <T en="Any active loans, mortgages, or bank liabilities that reduce your net wealth mathematically." ur="بینک سے لیا ہوا قرض یا ذاتی قرض، جو آپ کی ویلتھ کو کم کرتا ہو۔"/>
        </p>

        <div className="space-y-4">
            {liabilitiesArray.map((l:any, i:number) => (
              <div key={l.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end bg-white/80 p-4 rounded-2xl border border-rose-100 shadow-sm relative group">
                  <div className="md:col-span-4 space-y-1">
                    <Label className="text-[10px] font-bold uppercase text-slate-500"><T en="Categorization" ur="قسمِ قرضہ"/></Label>
                    <Select value={l.type} onValueChange={(v)=>arrayHandlers.updateLiability(l.id, 'type', v)}>
                        <SelectTrigger className="bg-white rounded-xl"><SelectValue/></SelectTrigger>
                        <SelectContent className="rounded-2xl">
                          <SelectItem value="Bank Loan" className="rounded-xl">Bank Loan</SelectItem>
                          <SelectItem value="Personal Debt" className="rounded-xl">Personal Debt</SelectItem>
                          <SelectItem value="Mortgage" className="rounded-xl">Mortgage</SelectItem>
                          <SelectItem value="Credit Card" className="rounded-xl">Credit Card</SelectItem>
                        </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-4 space-y-1">
                    <Label className="text-[10px] font-bold uppercase text-slate-500"><T en="Description" ur="قرضے کی تفصیل"/></Label>
                    <Input value={l.description} onChange={e=>arrayHandlers.updateLiability(l.id, 'description', e.target.value)} className="bg-white rounded-xl focus-visible:ring-rose-500/30" placeholder="E.g Auto Loan Alfalah"/>
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-[10px] font-bold uppercase text-rose-500"><T en="Remaining Obligation" ur="بقایا واجب الادا رقم"/></Label>
                    <Input type="number" value={l.amount||''} onChange={e=>arrayHandlers.updateLiability(l.id, 'amount', e.target.valueAsNumber)} className="bg-white rounded-xl font-bold text-rose-600 focus-visible:ring-rose-500/30"/>
                  </div>
                  <div className="md:col-span-1 absolute top-2 right-2 md:static">
                    <Button onClick={()=>arrayHandlers.removeLiability(l.id)} variant="ghost" className="w-8 h-8 md:w-full md:h-10 text-rose-500 hover:bg-rose-100 rounded-xl p-0 md:p-2"><Trash2 className="w-4 h-4"/></Button>
                  </div>
              </div>
            ))}
        </div>
        <Button type="button" onClick={arrayHandlers.addLiability} variant="outline" className="w-full border-dashed border-2 border-rose-200 text-rose-600 bg-white/50 hover:bg-white py-6 rounded-2xl font-bold transition-colors shadow-sm">
            <Plus className="w-5 h-5 mr-2"/> <T en="Declare Financial Liability" ur="نیا قرضہ شامل کریں"/>
        </Button>
      </div>

      <div className="bg-white/60 backdrop-blur-sm border border-slate-200/60 p-6 md:p-8 rounded-[2rem] shadow-sm space-y-2 mt-8">
        <Label className="font-bold text-slate-700"><T en="Other Contextual Details" ur="کوئی اور تفصیل (Other Contextual Details)"/></Label>
        <Textarea 
            value={formData.other_details || ''} 
            onChange={e=>updateFormData({other_details:e.target.value})} 
            className="h-24 bg-white/80 rounded-2xl focus:bg-white transition-colors border-slate-200 focus-visible:ring-indigo-500/30 p-4" 
            placeholder={lang==='ur'?"ایف بی آر آڈیٹرز کیلیئے کوئی خاص معلومات":"Use this securely if FBR auditors require specific string declarations..."}
        />
      </div>
    </div>
  );
};
